import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../supabase';
import { Cost } from '@shiftly/shared/src/schema';
import { DateTime } from 'luxon';

export type CostCreate = Omit<Cost, 'id' | 'created_at' | 'updated_at'>;

const SupabaseCostService = {
  async getCostsForMonth(month: DateTime): Promise<Cost[]> {
    try {
      const startOfMonth = month.startOf('month').toISO();
      const endOfMonth = month.endOf('month').toISO();
      
      const { data, error } = await supabase
        .from('costs')
        .select('*')
        .gte('date', startOfMonth)
        .lte('date', endOfMonth)
        .order('date', { ascending: true });

      if (error) {
        console.error('Error fetching costs:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        console.log('No documents found for costs in', month.toISO());
        return [];
      }

      return data as Cost[];
    } catch (error) {
      console.error('Error fetching costs:', error);
      throw error;
    }
  },

  async createCost(cost: CostCreate, month: DateTime): Promise<string> {
    const { data, error } = await supabase
      .from('costs')
      .insert({
        name: cost.name,
        set: cost.set,
        amount: cost.amount,
        category: cost.category,
        metadata: cost.metadata,
        date: month.toISO(),
      })
      .select()
      .single();

    if (error) throw error;
    return data.id;
  },

  async updateCost(id: string, cost: Partial<CostCreate>, month: DateTime): Promise<void> {
    const update_data: any = {};
    
    if (cost.name !== undefined) update_data.name = cost.name;
    if (cost.set !== undefined) update_data.set = cost.set;
    if (cost.amount !== undefined) update_data.amount = cost.amount;
    if (cost.category !== undefined) update_data.category = cost.category;
    if (cost.metadata !== undefined) update_data.metadata = cost.metadata;
    
    update_data.date = month.toISO();

    const { error } = await supabase
      .from('costs')
      .update(update_data)
      .eq('id', id);

    if (error) throw error;
  },

  async deleteCost(id: string): Promise<void> {
    const { error } = await supabase
      .from('costs')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  async removeExistingCostsByCategory(category: string, month: DateTime, metadata_filter?: { key: 'split_id' | 'notes'; value: string }): Promise<number> {
    try {
      const startOfMonth = month.startOf('month').toISO();
      const endOfMonth = month.endOf('month').toISO();
      
      console.log(`[CostService] Removing existing costs for category: ${category} in ${month.toFormat('yyyy-MM')}`);
      
      let query = supabase
        .from('costs')
        .select('*')
        .eq('category', category)
        .gte('date', startOfMonth)
        .lte('date', endOfMonth);

      const { data: costs, error: selectError } = await query;
      
      if (selectError) {
        console.error('Error fetching costs for removal:', selectError);
        throw selectError;
      }

      if (!costs || costs.length === 0) {
        console.log(`[CostService] No costs found for category ${category} in month ${month.toFormat('yyyy-MM')}`);
        return 0;
      }

      const costsToDelete = costs.filter((cost: Cost) => {
        if (metadata_filter && cost.metadata) {
          const metadataValue = cost.metadata[metadata_filter.key];
          return metadataValue && metadataValue.includes(metadata_filter.value);
        }
        return true;
      });

      if (costsToDelete.length === 0) {
        console.log(`[CostService] No costs matched filter criteria`);
        return 0;
      }

      const idsToDelete = costsToDelete.map(cost => cost.id);
      
      const { error: deleteError } = await supabase
        .from('costs')
        .delete()
        .in('id', idsToDelete);

      if (deleteError) {
        console.error('Error deleting costs:', deleteError);
        throw deleteError;
      }

      console.log(`[CostService] Successfully removed ${costsToDelete.length} costs`);
      return costsToDelete.length;
    } catch (error) {
      console.error('Error removing costs by category:', error);
      throw error;
    }
  }
};

export const useCosts = (month: DateTime) => {
  return useQuery({
    queryKey: ['supabase-costs', month.year, month.month],
    queryFn: () => SupabaseCostService.getCostsForMonth(month),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useCreateCost = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ cost, month }: { cost: CostCreate; month: DateTime }) =>
      SupabaseCostService.createCost(cost, month),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supabase-costs'] });
    }
  });
};

export const useUpdateCost = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ 
      id, 
      cost, 
      month
    }: { 
      id: string; 
      cost: Partial<CostCreate>; 
      month: DateTime;
    }) => SupabaseCostService.updateCost(id, cost, month),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supabase-costs'] });
    }
  });
};

export const useDeleteCost = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; }) => 
      SupabaseCostService.deleteCost(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supabase-costs'] });
    }
  });
};

export const useRemoveExistingCostsByCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ 
      category, 
      month, 
      metadata_filter 
    }: { 
      category: string; 
      month: DateTime; 
      metadata_filter?: { key: 'split_id' | 'notes'; value: string };
    }) => SupabaseCostService.removeExistingCostsByCategory(category, month, metadata_filter),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supabase-costs'] });
    }
  });
};

export default SupabaseCostService;